import { Link, useParams } from 'react-router-dom';
import {
  PhysicsIcon,
  ChemistryIcon,
  BiologyIcon,
  MathematicsIcon,
} from '../components/icons';
import styles from './SubjectDetail.module.css';

// Subject data configuration
const SUBJECT_DATA = {
  physics: {
    name: 'Physics',
    icon: PhysicsIcon,
    description: 'The study of matter, energy, and their interactions. Physics simulations at AxLabs solve real differential equations governing mechanical, electrical, thermal, and optical phenomena.',
    overview: [
      'Physics is the foundational science. Understanding forces, energy, and waves provides the framework for all other scientific disciplines.',
      'Our simulations start from Newton\'s laws and Maxwell\'s equations. We do not animate pre-baked outcomes—we compute the actual behavior of systems based on physical principles.',
      'Each experiment includes numerical stability indicators, showing students when approximations break down. This builds intuition about the limits of models.',
    ],
    curriculum: [
      {
        stage: 'Foundation',
        topics: [
          'Vectors and scalar quantities',
          'Kinematics in one and two dimensions',
          'Newton\'s laws of motion',
          'Work, energy, and power',
          'Linear momentum and collisions',
          'Rotational motion basics',
          'Simple harmonic motion',
          'Waves and wave properties',
          'Sound and standing waves',
        ],
      },
      {
        stage: 'Core',
        topics: [
          'Advanced dynamics and Lagrangian mechanics',
          'Gravitational fields and orbits',
          'Electric fields and Gauss\'s law',
          'Electric potential and capacitance',
          'DC and AC circuits',
          'Magnetic fields and forces',
          'Electromagnetic induction',
          'Thermodynamics and kinetic theory',
          'Geometric optics',
          'Physical optics and interference',
        ],
      },
      {
        stage: 'Advanced',
        topics: [
          'Special relativity foundations',
          'Quantum behavior and wave-particle duality',
          'Solid state physics basics',
          'Nuclear physics',
          'Computational physics methods',
          'Chaos and non-linear dynamics',
        ],
      },
    ],
    experiments: [
      { name: 'Pendulum Motion', domain: 'mechanics', description: 'Simple and compound pendulums with damping analysis.' },
      { name: 'Projectile Motion', domain: 'mechanics', description: 'Trajectories with air resistance and elevation variations.' },
      { name: 'Spring Oscillator', domain: 'mechanics', description: 'Damped and driven harmonic oscillators.' },
      { name: 'Collisions', domain: 'mechanics', description: 'Elastic and inelastic collisions in 1D and 2D.' },
      { name: 'Rotational Dynamics', domain: 'mechanics', description: 'Moment of inertia and angular momentum conservation.' },
      { name: 'Circuit Simulator', domain: 'electricity', description: 'DC/DC circuits with transient response analysis.' },
      { name: 'Electromagnetic Waves', domain: 'waves', description: 'Wave propagation and polarization.' },
      { name: 'Interference Patterns', domain: 'optics', description: 'Double-slit and diffraction experiments.' },
      { name: 'Heat Transfer', domain: 'thermodynamics', description: 'Conduction, convection, and radiation.' },
      { name: 'Ideal Gas', domain: 'thermodynamics', description: 'PV diagrams and thermodynamic processes.' },
    ],
    concepts: [
      'Conservation laws as symmetry principles',
      'Fields as fundamental entities',
      'Wave-particle duality',
      'Energy quantization',
      'Deterministic chaos',
      'Relativity of space and time',
      'Statistical nature of thermodynamics',
    ],
  },
  chemistry: {
    name: 'Chemistry',
    icon: ChemistryIcon,
    description: 'The study of matter and its transformations. Chemistry simulations model reaction kinetics, equilibrium, thermodynamics, and electrochemical processes using established rate laws and thermodynamic principles.',
    overview: [
      'Chemistry bridges physics and biology. Understanding molecular interactions, reaction mechanisms, and energy changes explains how matter transforms at the atomic scale.',
      'Our chemistry simulations use real rate laws, not arbitrary progress bars. Students can explore how concentration, temperature, and catalysts affect reaction outcomes.',
      'Equilibrium is treated dynamically—students see forward and reverse reactions competing toward steady states, matching how real chemical systems behave.',
    ],
    curriculum: [
      {
        stage: 'Foundation',
        topics: [
          'Atomic structure and periodic trends',
          'Chemical bonding basics',
          'Molecular geometry',
          'Stoichiometry and limiting reagents',
          'Types of chemical reactions',
          'Gas laws',
          'Solutions and concentration units',
          'Acids and bases basics',
        ],
      },
      {
        stage: 'Core',
        topics: [
          'Advanced bonding theories (VSEPR, MO)',
          'Thermochemistry and Hess\'s Law',
          'Chemical equilibrium calculations',
          'Le Chatelier\'s principle applications',
          'Acid-base equilibria and buffers',
          'Solubility equilibria',
          'Reaction kinetics and rate laws',
          'Electrochemistry and redox',
          'Thermodynamics (entropy, Gibbs energy)',
        ],
      },
      {
        stage: 'Advanced',
        topics: [
          'Quantum chemistry foundations',
          'Statistical thermodynamics',
          'Chemical kinetics mechanisms',
          'Photochemistry',
          'Coordination chemistry',
          'Biochemical pathways',
          'Computational chemistry methods',
        ],
      },
    ],
    experiments: [
      { name: 'Reaction Kinetics', domain: 'kinetics', description: 'Effect of concentration and temperature on reaction rates.' },
      { name: 'Chemical Equilibrium', domain: 'equilibrium', description: 'Dynamic equilibrium with perturbation analysis.' },
      { name: 'Titration Curves', domain: 'equilibrium', description: 'Strong/weak acid-base titrations with indicator selection.' },
      { name: 'Buffer Solutions', domain: 'equilibrium', description: 'pH stability and buffer capacity analysis.' },
      { name: 'Solubility Product', domain: 'equilibrium', description: 'Precipitation and common ion effect.' },
      { name: 'Electrochemical Cells', domain: 'electrochemistry', description: 'Galvanic and electrolytic cells with Nernst equation.' },
      { name: 'Thermochemistry', domain: 'thermochemistry', description: 'Enthalpy changes and calorimetry.' },
      { name: 'Gas Behavior', domain: 'stoichiometry', description: 'Ideal vs real gas behavior with compressibility.' },
    ],
    concepts: [
      'Molecular orbital theory',
      'Thermodynamics of reactions',
      'Dynamic equilibrium',
      'Reaction mechanisms',
      'Electron transfer principles',
      'Intermolecular forces',
      'Statistical nature of chemical behavior',
    ],
  },
  biology: {
    name: 'Biology',
    icon: BiologyIcon,
    description: 'The study of living systems. Biology simulations model population dynamics, enzyme kinetics, transport processes, and ecological interactions using differential equations and established biological models.',
    overview: [
      'Biology applies physical and chemical principles to living systems. Understanding these principles explains how organisms function, interact, and evolve.',
      'Our biology simulations use established models like the Michaelis-Menten equation, Lotka-Volterra equations, and Fick\'s laws. Students explore how parameters affect biological outcomes.',
      'Emphasis is on mechanistic understanding, not memorization. Students see how changing enzyme concentration affects reaction rates, or how predation affects population stability.',
    ],
    curriculum: [
      {
        stage: 'Foundation',
        topics: [
          'Cell structure and function',
          'Membrane transport basics',
          'Enzyme function',
          'Cellular respiration overview',
          'Photosynthesis overview',
          'Cell division (mitosis, meiosis)',
          'Basic genetics (Mendelian)',
          'Evolution by natural selection',
          'Ecological relationships',
        ],
      },
      {
        stage: 'Core',
        topics: [
          'Membrane transport mechanisms',
          'Enzyme kinetics and inhibition',
          'Metabolic pathways',
          'Population growth models',
          'Predator-prey dynamics',
          'Competition and niche theory',
          'Ecosystem energetics',
          'Physiological systems modeling',
          'Genetics and probability',
          'Hardy-Weinberg equilibrium',
        ],
      },
      {
        stage: 'Advanced',
        topics: [
          'Systems biology',
          'Biochemical networks',
          'Advanced population genetics',
          'Evolutionary game theory',
          'Neuroscience modeling',
          'Immunology modeling',
          'Developmental biology',
        ],
      },
    ],
    experiments: [
      { name: 'Enzyme Kinetics', domain: 'physiology', description: 'Michaelis-Menten parameters with inhibition analysis.' },
      { name: 'Population Growth', domain: 'ecology', description: 'Exponential and logistic growth with carrying capacity.' },
      { name: 'Predator-Prey', domain: 'ecology', description: 'Lotka-Volterra dynamics with phase plane analysis.' },
      { name: 'Membrane Transport', domain: 'cell-biology', description: 'Diffusion, osmosis, and active transport.' },
      { name: 'Cardiovascular Model', domain: 'physiology', description: 'Blood flow and pressure dynamics.' },
      { name: 'Respiratory Exchange', domain: 'physiology', description: 'Gas exchange and alveolar ventilation.' },
      { name: 'Ecosystem Succession', domain: 'ecology', description: 'Primary and secondary succession patterns.' },
      { name: 'Genetics Simulator', domain: 'genetics', description: 'Punnett squares and population allele frequencies.' },
    ],
    concepts: [
      'Structure-function relationship',
      'Homeostasis and feedback',
      'Energy flow in ecosystems',
      'Evolutionary optimization',
      'Stochasticity in biological systems',
      'Scaling laws in biology',
      'Network biology',
    ],
  },
  mathematics: {
    name: 'Mathematics',
    icon: MathematicsIcon,
    description: 'The language of patterns and abstraction. Mathematics simulations provide visual and interactive exploration of calculus, differential equations, linear algebra, and numerical analysis concepts.',
    overview: [
      'Mathematics underlies all scientific modeling. Our math simulations help students build intuition for abstract concepts through interactive visualization and experimentation.',
      'Each mathematical concept is presented both formally and intuitively. Students see the epsilon-delta definition of a limit alongside visual representations of approaching behavior.',
      'Numerical methods are emphasized alongside analytic solutions. Students learn when approximations are valid and how to estimate error bounds.',
    ],
    curriculum: [
      {
        stage: 'Foundation',
        topics: [
          'Functions and their properties',
          'Limits and continuity',
          'Derivatives as rates of change',
          'Basic integration',
          'Applications of derivatives',
          'Applications of integrals',
          'Sequences and series basics',
          'Vectors in 2D and 3D',
        ],
      },
      {
        stage: 'Core',
        topics: [
          'Advanced integration techniques',
          'Differential equations (first order)',
          'Differential equations (second order)',
          'Multivariable functions',
          'Partial derivatives',
          'Multiple integrals',
          'Vector calculus basics',
          'Matrices and linear transformations',
          'Eigenvalues and eigenvectors',
          'Probability and statistics',
        ],
      },
      {
        stage: 'Advanced',
        topics: [
          'Real analysis fundamentals',
          'Complex analysis',
          'Partial differential equations',
          'Numerical linear algebra',
          'Optimization theory',
          'Measure theory basics',
          'Stochastic processes',
        ],
      },
    ],
    experiments: [
      { name: 'Limit Visualizer', domain: 'calculus', description: 'Epsilon-delta definitions with interactive exploration.' },
      { name: 'Derivative Explorer', domain: 'calculus', description: 'Secant to tangent transition with rate interpretation.' },
      { name: 'Riemann Sums', domain: 'calculus', description: 'Left, right, midpoint, and trapezoidal approximations.' },
      { name: 'DE Solver', domain: 'differential-equations', description: 'First and second order ODEs with phase plots.' },
      { name: 'Vector Fields', domain: 'algebra', description: 'Gradient, divergence, and curl visualization.' },
      { name: 'Matrix Operations', domain: 'algebra', description: 'Transformations and eigensystem analysis.' },
      { name: 'Optimization', domain: 'numerical-analysis', description: 'Newton\'s method and gradient descent.' },
      { name: 'Fourier Series', domain: 'algebra', description: 'Function approximation with harmonic components.' },
      { name: 'Probability Distributions', domain: 'statistics', description: 'PDF, CDF, and expected value exploration.' },
    ],
    concepts: [
      'Limit as foundational concept',
      'Derivative as linear approximation',
      'Integral as accumulation',
      'Differential equations as dynamical laws',
      'Linearity as simplifying principle',
      'Convergence and approximation',
      'Invariance and symmetry',
    ],
  },
};

export function SubjectDetail() {
  const { subject } = useParams<{ subject: string }>();
  const data = subject && SUBJECT_DATA[subject as keyof typeof SUBJECT_DATA];

  if (!data) {
    return (
      <div className={styles.subjectDetail}>
        <div className={styles.errorContainer}>
          <h1 className={styles.errorTitle}>Subject Not Found</h1>
          <p className={styles.errorText}>
            The subject you're looking for doesn't exist.
          </p>
          <Link to="/subjects" className={styles.errorLink}>← Back to Subjects</Link>
        </div>
      </div>
    );
  }

  const IconComponent = data.icon;

  return (
    <div className={styles.subjectDetail}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerContainer}>
          <Link to="/subjects" className={styles.backLink}>← Back to Subjects</Link>
          <div className={styles.headerContent}>
            <div className={styles.headerIcon}>
              <IconComponent size={48} />
            </div>
            <div>
              <h1 className={styles.title}>{data.name}</h1>
              <p className={styles.headerDesc}>{data.description}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Overview */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Overview</h2>
          <div className={styles.overviewList}>
            {data.overview.map((paragraph, i) => (
              <p key={i} className={styles.overviewText}>{paragraph}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum */}
      <section className={styles.sectionAlt}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Curriculum</h2>
          <div className={styles.curriculumGrid}>
            {data.curriculum.map((stage) => (
              <div key={stage.stage} className={styles.curriculumCard}>
                <h3 className={styles.curriculumStage}>{stage.stage}</h3>
                <ul className={styles.curriculumList}>
                  {stage.topics.map((topic) => (
                    <li key={topic} className={styles.curriculumItem}>{topic}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Experiments */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Experiments</h2>
          <p className={styles.experimentsNote}>
            {data.experiments.length} core experiments covering {data.name.toLowerCase()} fundamentals.
            Each experiment produces deterministic, reproducible results for assessment.
          </p>
          <div className={styles.experimentsGrid}>
            {data.experiments.map((exp) => (
              <Link key={exp.name} to={`/experiments/${exp.domain}/${exp.name.toLowerCase().replace(/\s+/g, '-')}`} className={styles.experimentCard}>
                <span className={styles.experimentDomain}>{exp.domain}</span>
                <h3 className={styles.experimentName}>{exp.name}</h3>
                <p className={styles.experimentDesc}>{exp.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Core Concepts */}
      <section className={styles.sectionAlt}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Core Concepts</h2>
          <p className={styles.conceptsNote}>
            The {data.name.toLowerCase()} curriculum emphasizes these conceptual foundations
            that connect specific experiments to broader understanding.
          </p>
          <div className={styles.conceptsGrid}>
            {data.concepts.map((concept) => (
              <div key={concept} className={styles.conceptCard}>
                {concept}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContainer}>
          <Link to="/curriculum" className={styles.footerLink}>View Full Curriculum</Link>
          <span className={styles.footerSeparator}>·</span>
          <Link to="/experiments" className={styles.footerLink}>Browse All Experiments</Link>
          <span className={styles.footerSeparator}>·</span>
          <a href="https://github.com/staticpayload/axlabs" className={styles.footerLink} target="_blank" rel="noopener noreferrer">GitHub</a>
        </div>
      </footer>
    </div>
  );
}
